import type { AppContext, ChatEvent, ChatRequest } from '@cruz/shared';
import { readChatEvents } from './sse.ts';

export type ChatErrorKind = 'network' | 'rate_limit' | 'disabled' | 'server';

export class ChatError extends Error {
  override name = 'ChatError';
  readonly kind: ChatErrorKind;
  constructor(kind: ChatErrorKind) {
    super(kind);
    this.kind = kind;
  }
}

/** Sends the conversation and yields the answer events as they arrive. */
export async function* streamChat(
  messages: ChatRequest['messages'],
  signal?: AbortSignal,
): AsyncGenerator<ChatEvent> {
  let res: Response;
  try {
    res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ messages }),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new ChatError('network');
  }
  if (res.status === 429) throw new ChatError('rate_limit');
  if (res.status === 503) throw new ChatError('disabled');
  if (!res.ok || !res.body) throw new ChatError('server');
  try {
    yield* readChatEvents(res.body);
  } catch (err) {
    if (signal?.aborted) throw err;
    // The connection dropped mid-answer (bad coverage)
    throw new ChatError('network');
  }
}

export async function fetchContext(signal?: AbortSignal): Promise<AppContext> {
  const res = await fetch('/api/context', { signal });
  if (!res.ok) throw new Error(`context: HTTP ${res.status}`);
  return (await res.json()) as AppContext;
}
